import { uploadToTicket } from '../chunk-AGNB2ILC.js';
import { ASSET_FORMATS, ASSET_MIME, resolveChain, registry, AssetNamingError, CDN_ASSET_ROOT, assertValidAssetKey, assetFormatFromExtension } from '../chunk-WRZGIIO2.js';
export { ASSET_FORMATS, ASSET_MIME, ASSET_ROOT, AssetNamingError, CDN_ASSET_ROOT, DEFAULT_ASSET_FORMAT, PRIVATE_ASSET_ROOT, addressLogoKey, assertValidAssetKey, assetFormatFromExtension, assetUrl, catalogLogoKey, chainLogoKey, clearAssetCdn, configureAssetCdn, configuredAssetCdnBase, entityLogoKey, groupLogoKey, isAssetFormat, isValidAssetSlug, labelLogoKey, mimeForAssetFormat, normalizeAddressForKey, publicAssetUrl, slugifyAsset, tagLogoKey, tenantLogoKey, tokenLogoKey } from '../chunk-WRZGIIO2.js';
import '../chunk-GPJ572YS.js';
import '../chunk-N7SZ66QO.js';
import '../chunk-PZ5AY32C.js';

// src/assets/cdn.ts
var CDN_KIND_DIR = {
  chain: "blockchain",
  token: "tokens",
  nft: "nfts",
  protocol: "protocols"
};
var LOGO_VARIANT_DIR = "logos";
var DEFAULT_FORMAT_BY_KIND = {
  chain: "svg",
  protocol: "svg"
};
function stemForSlug(slug) {
  const stem = slug.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  if (!stem) throw new AssetNamingError("Asset identifier has no usable characters");
  return stem;
}
function cdnChainLogoKey(input) {
  const ext = input.format ?? DEFAULT_FORMAT_BY_KIND.chain;
  const value = input.slug.trim().toLowerCase();
  const numeric = /^(0x[0-9a-f]+|[0-9]+)$/.test(value) && BigInt(value) > 0n ? `eip155:${BigInt(value)}` : value;
  const chain = resolveChain(numeric) ?? registry.all().find((entry) => entry.name.toLowerCase() === value);
  const stem = chain ? stemForSlug(chain.slug) : numeric.replace(/:/g, "_");
  if (!/^[a-z0-9][a-z0-9_.-]*$/.test(stem)) throw new AssetNamingError("Invalid chain asset identifier");
  const key = `${CDN_ASSET_ROOT}/${CDN_KIND_DIR.chain}/${LOGO_VARIANT_DIR}/${ext}/${stem}.${ext}`;
  assertValidAssetKey(key);
  return key;
}
function cdnProtocolLogoKey(input) {
  const ext = input.format ?? DEFAULT_FORMAT_BY_KIND.protocol;
  const stem = stemForSlug(input.id);
  const category = input.category ? `${stemForSlug(input.category)}/` : "";
  const key = `${CDN_ASSET_ROOT}/${CDN_KIND_DIR.protocol}/${LOGO_VARIANT_DIR}/${ext}/${category}${stem}.${ext}`;
  assertValidAssetKey(key);
  return key;
}

// src/assets/upload.ts
var ASSET_UPLOAD_TICKET_PATH = "/assets/upload-ticket";
var DEFAULT_MAX_LOGO_BYTES = 512 * 1024;
var DEFAULT_ACCEPTED_LOGO_MIME = ASSET_FORMATS.map((f) => ASSET_MIME[f]);
function validateLogoFile(file, options = {}) {
  const { maxBytes = DEFAULT_MAX_LOGO_BYTES, acceptMime = DEFAULT_ACCEPTED_LOGO_MIME } = options;
  const format = assetFormatFromExtension(file.name) ?? mimeToFormat(file.type);
  if (!format) {
    return { ok: false, error: `Unsupported file type (allowed: ${ASSET_FORMATS.join(", ")})` };
  }
  if (file.type && !acceptMime.includes(file.type)) {
    return { ok: false, error: `Unsupported MIME type "${file.type}"`, format };
  }
  if (file.size > maxBytes) {
    return { ok: false, error: `File too large (max ${Math.round(maxBytes / 1024)} KB)`, format };
  }
  if (!Number.isSafeInteger(file.size) || file.size <= 0) {
    return { ok: false, error: "File must contain image bytes", format };
  }
  return { ok: true, format };
}
function mimeToFormat(mime) {
  if (!mime) return void 0;
  return Object.keys(ASSET_MIME).find((f) => ASSET_MIME[f] === mime);
}
async function readImageDimensions(file) {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file);
    try {
      return { width: bitmap.width, height: bitmap.height };
    } finally {
      bitmap.close();
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image dimensions"));
    };
    img.src = url;
  });
}
async function requestAssetUploadTicket(client, input, path = ASSET_UPLOAD_TICKET_PATH) {
  const res = await client.post(path, {
    kind: input.kind,
    resource_id: input.id,
    chain: input.chain,
    ...input.scope ? { scope: input.scope } : {},
    content_type: input.contentType,
    size_bytes: input.sizeBytes
  });
  if (!res.ok) throw new Error(`[assets] upload ticket request failed: ${res.error.message}`);
  const d = res.data ?? {};
  if (!d.key || !d.upload_key || !d.upload_url && !d.post_url) {
    throw new Error("[assets] upload ticket response missing key / upload key / upload URL");
  }
  return {
    key: d.key,
    uploadKey: d.upload_key,
    bucket: d.bucket,
    uploadUrl: d.upload_url,
    postUrl: d.post_url,
    fields: d.fields,
    headers: d.headers,
    expiresAt: d.expires_at
  };
}
async function uploadLogo(client, input, options = {}) {
  const validation = validateLogoFile(input.file, options.validate);
  if (!validation.ok) throw new Error(validation.error ?? "Invalid logo file");
  const ticket = await requestAssetUploadTicket(
    client,
    {
      kind: input.kind,
      id: input.id,
      chain: input.chain,
      scope: input.scope,
      contentType: input.file.type || ASSET_MIME[validation.format ?? "png"],
      sizeBytes: input.file.size,
      format: validation.format
    },
    options.path
  );
  await uploadToTicket(ticket, input.file, { signal: options.signal });
  const finalized = await client.post(options.path ?? ASSET_UPLOAD_TICKET_PATH, {
    action: "finalize",
    kind: input.kind,
    resource_id: input.id,
    chain: input.chain,
    ...input.scope ? { scope: input.scope } : {},
    content_type: input.file.type || ASSET_MIME[validation.format ?? "png"],
    upload_key: ticket.uploadKey
  });
  if (!finalized.ok) throw new Error(`[assets] upload validation failed: ${finalized.error.message}`);
  if (!finalized.data?.key) throw new Error("[assets] upload validation response missing key");
  return { key: finalized.data.key, cdnUrl: finalized.data.cdn_url };
}

export { ASSET_UPLOAD_TICKET_PATH, CDN_KIND_DIR, DEFAULT_ACCEPTED_LOGO_MIME, DEFAULT_MAX_LOGO_BYTES, cdnChainLogoKey, cdnProtocolLogoKey, readImageDimensions, requestAssetUploadTicket, uploadLogo, validateLogoFile };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map